import React, { useState } from "react";
import { Eye, EyeOff, Mail, Lock, ArrowRight, User, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import BaseApi from "../services/base-api";
import { useToast } from "../context/ToastContext";
import { ALERT_STATUS, showAlert } from "../components/Modals/alert";
import { useTranslation } from "react-i18next";
import AppLogo from "../components/AppLogo";

const SignUp = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const navigate = useNavigate();
  const { success, err } = useToast();
  const { i18n, t } = useTranslation();

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string) => {
    const phoneRegex = /^[0-9]{10,15}$/;
    return phoneRegex.test(phone.replace(/[\s-]/g, ""));
  };

  const validateForm = () => {
    const newErrors = {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    };

    if (!formData.firstName.trim()) {
      newErrors.firstName = t("First name is required");
    } else if (formData.firstName.trim().length < 3) {
      newErrors.firstName = t("First Name must be at least 3 characters");
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t("Last name is required");
    } else if (formData.lastName.trim().length < 1) {
      newErrors.lastName = t("Last Name must be at least 1 characters");
    }

    if (!formData.email) {
      newErrors.email = t("Email is required");
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t("Please enter a valid email address");
    }

    if (!formData.phone) {
      newErrors.phone = t("Phone number is required");
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = t("Please enter a valid phone number (10-15 digits)");
    }

    if (!formData.password) {
      newErrors.password = t("Password is required");
    } else if (formData.password.length < 8) {
      newErrors.password = t("Password must be at least 8 characters");
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = t(
        "Password must contain uppercase, lowercase, and number",
      );
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t("Please confirm your password");
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t("Passwords do not match");
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => !error);
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!agreedToTerms) {
      showAlert(
        ALERT_STATUS.WARNING,
        t("Please Agree"),
        t("Please agree to the Terms of Service and Privacy Policy"),
      );
      return;
    }

    if (validateForm()) {
      setIsLoading(true);

      try {
        // Exclude confirmPassword before sending
        const { confirmPassword, ...payload } = formData;
        const res = await BaseApi.post("/auth/register", payload);
        // console.log("Sign Up response ----> ", res);

        if (res) {
          success(t("Your account has been created successfully!"));
          navigate("/signin");
        } else {
          err(res.message || t("Failed to create account. Please try again."));
        }
      } catch (error) {
        console.error(t("Failed to create account. Please try again."));
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
      <div className="w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-0 min-h-screen lg:min-h-[700px]">
        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes slideInLeft {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.05);
            }
          }
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-20px);
            }
          }
        `}</style>

        {/* Left Side - Branding Section */}
        <div
          className="hidden lg:flex bg-gradient-to-br from-black via-gray-700 to-black p-12 flex-col justify-center items-center relative overflow-hidden"
          style={{
            animation: "slideInLeft 0.8s ease-out",
          }}
        >
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div
              className="absolute top-10 left-10 w-32 h-32 border-2 border-white rounded-full"
              style={{ animation: "float 6s ease-in-out infinite" }}
            ></div>
            <div
              className="absolute top-40 right-20 w-20 h-20 border-2 border-white rounded-full"
              style={{ animation: "float 8s ease-in-out infinite 1s" }}
            ></div>
            <div
              className="absolute bottom-20 left-20 w-40 h-40 border-2 border-white rounded-full"
              style={{ animation: "float 7s ease-in-out infinite 2s" }}
            ></div>
            <div
              className="absolute bottom-40 right-10 w-24 h-24 border-2 border-white rounded-full"
              style={{ animation: "float 9s ease-in-out infinite 1.5s" }}
            ></div>
          </div>

          <div className="relative z-10 text-center">
            {/* Logo */}
            <div
              className="flex justify-center mb-8"
              style={{
                animation: "pulse 2s ease-in-out infinite",
              }}
            >
              <AppLogo className="w-32 h-32 bg-white rounded-2xl lg-shadow" />
            </div>

            <h1 className="text-5xl font-bold text-white mb-4">
              {t("Join Maagoud")}
            </h1>
            <p className="text-gray-300 text-lg mb-8 max-w-md">
              {t(
                "Create your account today and experience premium shopping like never before.",
              )}
            </p>

            {/* Benefits */}
            <div className="space-y-4 text-left max-w-md">
              <div className="flex items-start gap-3">
                <div className="bg-white/10 rounded-lg p-2 mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">
                    {t("Exclusive Deals")}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t("Access member-only discounts and offers")}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-white/10 rounded-lg p-2 mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">
                    {t("Easy Checkout")}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t("Save your info for lightning-fast purchases")}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-white/10 rounded-lg p-2 mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">
                    {t("Order Tracking")}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t("Track your orders in real-time")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form Section */}
        <div
          className="flex items-center justify-center p-6 sm:p-8 lg:p-12 bg-white overflow-y-auto"
          style={{
            animation: "slideInRight 0.8s ease-out",
          }}
        >
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <AppLogo className="w-32 h-32 bg-white rounded-2xl lg-shadow" />

            <div className="mb-6">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                {t("Create Account")}
              </h2>
              <p className="text-gray-600 text-base">
                {t("Fill in your details to get started")}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* First Name Field */}
              <div
                style={{
                  animation: "slideInRight 0.5s ease-out 0.2s backwards",
                }}
              >
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("First Name")}
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    className={`w-full pl-12 pr-4 py-3.5 rounded-xl border-2 ${
                      errors.firstName
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-200 focus:border-black"
                    } focus:outline-none transition-all duration-300 text-base`}
                    placeholder={t("Enter your first name")}
                  />
                </div>
                {errors.firstName && (
                  <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                    <span className="text-lg">•</span> {errors.firstName}
                  </p>
                )}
              </div>

              {/* Last Name Field */}
              <div
                style={{
                  animation: "slideInRight 0.5s ease-out 0.2s backwards",
                }}
              >
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Last Name")}
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    className={`w-full pl-12 pr-4 py-3.5 rounded-xl border-2 ${
                      errors.lastName
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-200 focus:border-black"
                    } focus:outline-none transition-all duration-300 text-base`}
                    placeholder={t("Enter your last name")}
                  />
                </div>
                {errors.lastName && (
                  <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                    <span className="text-lg">•</span> {errors.lastName}
                  </p>
                )}
              </div>

              {/* Email Field */}
              <div
                style={{
                  animation: "slideInRight 0.5s ease-out 0.3s backwards",
                }}
              >
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Email Address")}
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className={`w-full pl-12 pr-4 py-3.5 rounded-xl border-2 ${
                      errors.email
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-200 focus:border-black"
                    } focus:outline-none transition-all duration-300 text-base`}
                    placeholder={t("Enter your email")}
                  />
                </div>
                {errors.email && (
                  <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                    <span className="text-lg">•</span> {errors.email}
                  </p>
                )}
              </div>

              {/* Phone Field */}
              <div
                style={{
                  animation: "slideInRight 0.5s ease-out 0.4s backwards",
                }}
              >
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Phone Number")}
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    className={`w-full pl-12 pr-4 py-3.5 rounded-xl border-2 ${
                      errors.phone
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-200 focus:border-black"
                    } focus:outline-none transition-all duration-300 text-base`}
                    placeholder="Enter your phone number"
                  />
                </div>
                {errors.phone && (
                  <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                    <span className="text-lg">•</span> {errors.phone}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div
                style={{
                  animation: "slideInRight 0.5s ease-out 0.5s backwards",
                }}
              >
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Password")}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) =>
                      handleInputChange("password", e.target.value)
                    }
                    className={`w-full pl-12 pr-14 py-3.5 rounded-xl border-2 ${
                      errors.password
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-200 focus:border-black"
                    } focus:outline-none transition-all duration-300 text-base`}
                    placeholder={t("Create a password")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                    <span className="text-lg">•</span> {errors.password}
                  </p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div
                style={{
                  animation: "slideInRight 0.5s ease-out 0.6s backwards",
                }}
              >
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Confirm Password")}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) =>
                      handleInputChange("confirmPassword", e.target.value)
                    }
                    className={`w-full pl-12 pr-14 py-3.5 rounded-xl border-2 ${
                      errors.confirmPassword
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-200 focus:border-black"
                    } focus:outline-none transition-all duration-300 text-base`}
                    placeholder={t("Confirm your password")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                    <span className="text-lg">•</span> {errors.confirmPassword}
                  </p>
                )}
              </div>

              {/* Terms and Conditions */}
              <div
                className="flex items-start gap-3"
                style={{
                  animation: "slideInRight 0.5s ease-out 0.7s backwards",
                }}
              >
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded border-2 border-gray-300 text-black focus:ring-2 focus:ring-black cursor-pointer"
                />
                <label className="text-sm text-gray-700 cursor-pointer">
                  {t("I agree to the")}{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/terms")}
                    className="text-black font-semibold hover:underline"
                  >
                    {t("Terms of Service")}
                  </button>{" "}
                  {t("and")}{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/privacy-policy")}
                    className="text-black font-semibold hover:underline"
                  >
                    {t("Privacy Policy")}
                  </button>
                </label>
              </div>

              {/* Submit Button */}
              <div
                style={{
                  animation: "slideInRight 0.5s ease-out 0.8s backwards",
                }}
              >
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full bg-black text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 text-base ${
                    isLoading
                      ? "opacity-70 cursor-not-allowed"
                      : "hover:bg-gray-700 hover:shadow-2xl transform hover:-translate-y-1 active:scale-95"
                  }`}
                >
                  {isLoading ? (
                    <>
                      <div
                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                        style={{
                          animation: "spin 1s linear infinite",
                        }}
                      ></div>
                      <span>{t("Creating Account...")}</span>
                    </>
                  ) : (
                    <>
                      <span>{t("Create Account")}</span>
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Divider */}
            <div
              className="flex items-center gap-4 my-6"
              style={{
                animation: "slideInRight 0.5s ease-out 0.9s backwards",
              }}
            >
              <div className="flex-1 h-px bg-gray-300"></div>
              <span className="text-sm text-gray-500 font-medium">OR</span>
              <div className="flex-1 h-px bg-gray-300"></div>
            </div>

            {/* Sign In Link */}
            <div
              className="text-center"
              style={{
                animation: "slideInRight 0.5s ease-out 1s backwards",
              }}
            >
              <p className="text-base text-gray-600">
                {t("Already have an account?")}{" "}
                <button
                  type="button"
                  onClick={() => navigate("/signin")}
                  className="font-semibold text-black hover:text-gray-700 transition-colors underline decoration-2 underline-offset-2"
                >
                  {t("Sign In")}
                </button>
              </p>
            </div>

            {/* Footer Text */}
            <div
              className="text-center mt-6"
              style={{
                animation: "slideInRight 0.5s ease-out 1.1s backwards",
              }}
            >
              <p className="text-xs text-gray-500">
                {t("Protected by industry-standard encryption")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
